/**
 * Shared utility functions for the Helix admin frontend.
 *
 * Functions:
 *   cn(...classes)             — clsx + tailwind-merge class name helper
 *   formatDate(date, locale)   — Intl.DateTimeFormat wrapper → "Jan 15, 2025"
 *   formatRelativeTime(date)   — "2 hours ago" style relative time
 *   formatDuration(ms)         — "2m 34s" style from milliseconds
 *   formatBytes(bytes)         — "1.2 MB" style
 *   truncate(str, maxLen)      — "Hello Wor..." style (ellipsis at maxLen)
 *   maskApiKey(key)            — "sk-...abc" mask first chars, show last 4
 *   groupBy<T>(arr, keyFn)     — group array elements by a key function
 *   debounce(fn, delay)        — utility debounce (non-hook, for callbacks)
 *   sleep(ms)                  — Promise-based delay
 *   isRTL(lang)                — returns true for RTL languages ('ar')
 *   clamp(value, min, max)     — constrain a number to a range
 *   omit<T>(obj, keys)         — create object without specified keys
 *   pick<T>(obj, keys)         — create object with only specified keys
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ---------------------------------------------------------------------------
// Tailwind class merger
// ---------------------------------------------------------------------------

/**
 * Merge Tailwind CSS classes with conflict resolution.
 * Uses clsx for conditional classes and tailwind-merge to deduplicate.
 *
 * @example
 *   cn('px-4 py-2', isActive && 'bg-violet-600', className)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// Date / time formatting
// ---------------------------------------------------------------------------

/**
 * Format a date value to a localized string.
 * @param date   — Date object, ISO string, or Unix timestamp (ms)
 * @param locale — BCP 47 locale string (default: browser locale)
 * @param options — Intl.DateTimeFormatOptions (default: medium date)
 */
export function formatDate(
  date: Date | string | number,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = date instanceof Date ? date : new Date(date);
  const opts: Intl.DateTimeFormatOptions = options ?? {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  return new Intl.DateTimeFormat(locale ?? navigator.language, opts).format(d);
}

/**
 * Format a date as a relative time string ("2 hours ago", "in 3 days").
 * Falls back to formatDate() for dates older than 7 days.
 */
export function formatRelativeTime(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  const diff = Date.now() - d.getTime(); // positive = past
  const absDiff = Math.abs(diff);

  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const WEEK = 7 * DAY;

  const rtf = new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' });

  if (absDiff < MINUTE) return 'just now';
  if (absDiff < HOUR) return rtf.format(-Math.round(diff / MINUTE), 'minute');
  if (absDiff < DAY) return rtf.format(-Math.round(diff / HOUR), 'hour');
  if (absDiff < WEEK) return rtf.format(-Math.round(diff / DAY), 'day');

  return formatDate(d);
}

/**
 * Format a duration in milliseconds to a human-readable string.
 * @example formatDuration(154_000) → "2m 34s"
 * @example formatDuration(45_000)  → "45s"
 * @example formatDuration(3_600_000) → "1h 0m"
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// ---------------------------------------------------------------------------
// File size formatting
// ---------------------------------------------------------------------------

/**
 * Format bytes to a human-readable string.
 * @example formatBytes(1_200_000) → "1.2 MB"
 * @example formatBytes(512) → "512 B"
 */
export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  return `${value.toFixed(i === 0 ? 0 : decimals)} ${units[i]}`;
}

// ---------------------------------------------------------------------------
// String utilities
// ---------------------------------------------------------------------------

/**
 * Truncate a string to maxLen characters, appending an ellipsis.
 * @example truncate("Hello World", 8) → "Hello..."
 */
export function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 3) + '...';
}

/**
 * Mask an API key for display — shows last 4 characters.
 * @example maskApiKey("sk-abc123def456") → "sk-...f456"
 * @example maskApiKey("short") → "•••••"
 */
export function maskApiKey(key: string): string {
  if (!key || key.length < 8) return '•'.repeat(key?.length ?? 5);
  const prefix = key.startsWith('sk-') ? 'sk-' : key.slice(0, 3);
  const last4 = key.slice(-4);
  return `${prefix}...${last4}`;
}

// ---------------------------------------------------------------------------
// Array utilities
// ---------------------------------------------------------------------------

/**
 * Group an array by a key function.
 * @example
 *   groupBy([{type:'a'},{type:'b'},{type:'a'}], x => x.type)
 *   // → { a: [{type:'a'},{type:'a'}], b: [{type:'b'}] }
 */
export function groupBy<T>(
  arr: T[],
  keyFn: (item: T) => string,
): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyFn(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

// ---------------------------------------------------------------------------
// Function utilities
// ---------------------------------------------------------------------------

/**
 * Non-hook debounce utility for event callbacks.
 * Returns a debounced version of fn that delays invocation by `delay` ms.
 * For React state, prefer the useDebounce hook instead.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

/**
 * Promise-based sleep.
 * @example await sleep(1000); // wait 1 second
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Locale / RTL utilities
// ---------------------------------------------------------------------------

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur']);

/**
 * Returns true if the given language code uses right-to-left layout.
 * @example isRTL('ar') → true
 * @example isRTL('en') → false
 */
export function isRTL(lang: string): boolean {
  return RTL_LANGS.has(lang.split('-')[0].toLowerCase());
}

// ---------------------------------------------------------------------------
// Number utilities
// ---------------------------------------------------------------------------

/**
 * Clamp a number between a minimum and maximum value.
 * @example clamp(150, 0, 100) → 100
 * @example clamp(-5, 0, 1) → 0
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ---------------------------------------------------------------------------
// Object utilities
// ---------------------------------------------------------------------------

/**
 * Create a shallow copy of an object without the specified keys.
 */
export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((k) => delete result[k]);
  return result as Omit<T, K>;
}

/**
 * Create a shallow copy of an object with only the specified keys.
 */
export function pick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach((k) => {
    if (k in obj) result[k] = obj[k];
  });
  return result;
}

/**
 * Generate a simple random ID (not cryptographically secure).
 * Use for React keys, not for security-sensitive IDs.
 */
export function nanoid(length = 12): string {
  return Math.random().toString(36).slice(2, 2 + length);
}
